<template>
    <div style="display: flex;">

        <!-- Select dropdown menu for selecting country code. -->
        <zoezi-autocomplete
            v-model="selectedCountryCode"
            :items="langCodeOptions"
            :item-text="searchKeyCountryCode"
            item-value="key"
            :prepend-icon="icon"
            style="width: 8rem;"
            @change="updatePhoneNumber"
        >
            <template v-slot:selection="{ item }">
                <div class="select-language">
                    <img :src="getFlag(item.key)" class="flag-icon" />
                    {{ item.phone[0] }}
                </div>
            </template>
            <template v-slot:item="{ item }">
                <img :src="getFlag(item.key)" class="flag-icon" />
                <div>
                    ({{ item.phone[0] }}) {{ $translate(item.name) }}
                </div>
            </template>
        </zoezi-autocomplete>

        <!-- Textarea for the phone number. -->
        <v-text-field :label="$translate(label)" type="tel" inputmode="tel" v-model="number" :rules="translatedInternalRules" :error-messages="errorMessages" style="margin-left: 0.5rem;" @blur="hasBeenFocused = true; refreshInternalRules(phoneNumber)">
            <template #append>
                <v-icon v-if="starRequired" small :color="valueOk ? 'success' : 'error'">{{valueOk ? 'mdi-check' : 'mdi-star'}}</v-icon>
            </template>
        </v-text-field>

    </div>
</template>

<script>
import phoneExtensions from './phoneExtensions.json';
import ZoeziAutocomplete from '../ui/form/Autocomplete.vue';

export default {
    name: 'PhoneField',
    components: {
        ZoeziAutocomplete,
    },
    props: {
        label: {
            type: String,
            required: true
        },
        value: {
            type: String
        },
        icon: {
            type: String
        },
        starRequired: {
            type: Boolean
        },
        rules: [],
        errorMessages: []
    },
    data() {
        return {
            selectedCountryCode: '',
            number: '',
            phoneNumber: '',
            internalRules: [],
            hasBeenFocused: false
        }
    },
    methods: {
        searchKeyCountryCode(contryCode) {
            return `${contryCode.name}${contryCode.phone.join('')}`;
        },
        emitValue(value) {
            this.$emit('input', value);
        },
        getFlag(langCode) {
            return phoneExtensions[langCode].image;
        },
        getMostLikelyCountryCode() {
            // Guess the users countrycode by using
            // the free ip => country api at ip2c.org then
            // mark it in as selected in the select dropdown.
            fetch('https://ip2c.org/s')
                .then(response => response.text())
                .then(text => {
                    const [status, countryCode] = text.split(';');

                    if (status === '1') {
                        this.selectedCountryCode = countryCode;
                        this.number = this.value;
                        this.updatePhoneNumber();
                    } else {
                        console.error('Could not determine the country code.');
                    }
                })
                .catch(error => {
                    console.error('Error fetching the country code:', error);
                });
        },
        refreshInternalRules(number = null) {
            if (!this.hasBeenFocused) return;
            this.internalRules = this.rules.map(ruleFunction => ruleFunction(number));
            // If we have entered something in the number field then
            // we want the "Incorrect phone number" rule to be first
            if (this.number?.length > 0) this.internalRules.reverse();
        },
        updatePhoneNumber() {
            // Check if a number is entered that contains things other than
            // numbers using regex matching. Less strain on backend.
            if (this.number && this.number.length > 0 && !/^\d+$/.test(this.number)) this.phoneNumber = null;

            this.$api.get('/api/public/verify/phonenumber', { number: this.number, countrycode: this.selectedCountryCode }).then(res => {
                if (!res.error) {
                    this.phoneNumber = res.number;
                }
                else { // Not valid phone number or an error occured.
                    this.phoneNumber = null;
                }
            })
        }
    },
    computed: {
        langCodeOptions() {
            return Object.entries(phoneExtensions).map(([key, value]) => ({ key, ...value }));
        },
        valueOk() {
            if (this.internalRules.length === 0) return false;
            return !this.internalRules.some(rule => typeof rule == 'string' || rule === false);
        },
        translatedInternalRules() {
            return this.internalRules.map(rule => typeof rule == 'string' ? this.$translate(rule) : rule);
        }
    },
    mounted() {
        const getCountryCode = async () => {
            // Try to verify current value with backend.
            // This will work if it is in E164 format.
            const res = await this.$api.get('/api/public/verify/phonenumber', { number: this.value });

            // If it parsed successfully.
            if (!res.error) {
                this.number = res.number;
                this.selectedCountryCode = res.countrycode;
            }
            // If parsing fails, guess countrycode by IP number.
            else {
                this.getMostLikelyCountryCode();
            }
        }
        getCountryCode();
    },
    watch: {
        phoneNumber: function (newVal) {
            this.emitValue(newVal);
            this.refreshInternalRules(newVal);
        },
        number: function () {
            this.updatePhoneNumber();
            this.refreshInternalRules(this.phoneNumber);
        }
    }
};
</script>

<style scoped>
.flag-icon {
    width: 24px;
    height: 16px;
    margin-right: 8px;
}

.select-language {
    display: flex;
    align-items: center;
}
</style>
